import type { FunctionComponent } from 'react';
import { useState } from 'react';

import { useTranslation } from 'i18n';

import { Badge, Button, Divider, Group, Image, Modal, Stack, Text, Textarea } from '@mantine/core';

import { useForm } from '@mantine/form';
import { useCounter } from '@mantine/hooks';

import Counter from 'primitives/Counter/Counter';
import { Dock } from 'primitives/Dock/Dock';

import {
  buildVisibleModifierGroups,
  deselectModifier,
  isCheckboxValue,
  isInputGroupValue,
  isModifierGroupRequirementMet,
  isRadioGroupValue,
} from 'helpers/menuPreview';
import type {
  GetSortedChildren,
  ItemEntry,
  ModifierGroupFormValue,
  PreviewMenu,
  PreviewModifier,
  PreviewModifierGroup,
} from 'helpers/menuPreview';
import { formatCurrency } from 'helpers/format.cuboh';
import {
  buildAvailableClassifications,
  DEFAULT_IMAGE_FILE_NAME,
  ERROR_IMAGE_FILE_NAME,
  MENU_TYPES,
} from 'constants/menuManagement';
import { useMenuManagement } from 'hooks/useMenuManagement';

import { ModifierGroup } from 'components/MenuManagement/MenuPreview';

const NS = 'menuManagementPreviewModal';

type PreviewItem = ItemEntry & {
  name?: string;
  description?: string;
  imageUrl?: string;
  servingsTag?: string;
  paused?: boolean;
  price?: { price?: number };
  classifications?: Array<{ isDeleted?: boolean; itemClass?: string }>;
};

type PreviewItemFormValues = {
  instructions: string;
  modifiers: Record<string, ModifierGroupFormValue | undefined>;
};

type MenuManagementPreviewItemModalProps = {
  mainItem?: string;
  menu?: PreviewMenu;
  getSortedChildren: GetSortedChildren;
  onActionFinish: () => void;
  opened: boolean;
  onClose: () => void;
};

export const MenuManagementPreviewItemModal: FunctionComponent<
  MenuManagementPreviewItemModalProps
> = ({ mainItem = '', menu, getSortedChildren, onActionFinish, opened, onClose }) => {
  const { t } = useTranslation(NS);
  const [showErrors, setShowErrors] = useState(false);
  const [activeNestedPath, setActiveNestedPath] = useState<
    Array<{ modifierKey: string; modifierName: string }>
  >([]);
  const activeNestedModifier = activeNestedPath[activeNestedPath.length - 1] ?? null;
  const { getAvailableClassificationsFromState } = useMenuManagement();
  const classificationsMap = (() => {
    const raw = buildAvailableClassifications(getAvailableClassificationsFromState());
    const lookup: Record<string, string> = {};
    Object.entries(raw).forEach(([key, value]) => {
      lookup[key] = value.label;
      if (value.classification) lookup[value.classification] = value.label;
    });
    return lookup;
  })();
  const itemDetails: PreviewItem | undefined = menu?.items[mainItem];

  const [quantity, { increment, decrement, set }] = useCounter(1, { min: 1, max: 99 });
  const isCateringMenu = menu?.menuType === MENU_TYPES.CATERING;

  const servingsTag = isCateringMenu ? itemDetails?.servingsTag : null;
  const servingsLabel = servingsTag
    ? t('MenuManagementPreviewItemModal.servingsLabel', { servingsTag })
    : null;

  const classificationBadges = (itemDetails?.classifications || [])
    .filter(c => !c.isDeleted)
    .map(c => (c.itemClass ? classificationsMap[c.itemClass] : undefined))
    .filter(Boolean);

  const titleImage = itemDetails?.imageUrl && (
    <Image
      src={itemDetails?.imageUrl}
      alt={itemDetails?.name}
      fallbackSrc={
        itemDetails?.imageUrl
          ? `${import.meta.env.VITE_CUBOH_ASSET_URL}/${ERROR_IMAGE_FILE_NAME}`
          : `${import.meta.env.VITE_CUBOH_ASSET_URL}/${DEFAULT_IMAGE_FILE_NAME}`
      }
      radius="sm"
    />
  );

  const form = useForm<PreviewItemFormValues>({
    initialValues: {
      instructions: '',
      modifiers: {},
    },
    validateInputOnBlur: true,
    validate: {
      modifiers: ((modifiers: Record<string, ModifierGroupFormValue>) => {
        const errors: Record<string, string> = {};
        const visibleModifierGroups = buildVisibleModifierGroups({
          itemKey: itemDetails?.key,
          itemType: 'items',
          menu: menu as PreviewMenu,
          getSortedChildren,
          formModifiers: modifiers,
        });
        visibleModifierGroups.forEach(group => {
          const { groupKey } = group;
          const groupModifiers = modifiers[groupKey];

          // Validation for radio group
          if ((group.minimum ?? 0) > 0 && group.maximum === 1) {
            if (!groupModifiers) {
              errors[groupKey] = t('MenuManagementPreviewItemModal.validationSelectOption', {
                groupName: group.name,
              });
            }
          }

          // Validation for input group
          if ((group.minimum ?? 0) > 0 && (group.maximum ?? 0) > 1 && group.maximum !== null) {
            const totalQuantity = Object.values(
              (groupModifiers as Record<string, { quantity: number }>) || {},
            ).reduce((acc, mod) => acc + mod.quantity, 0);
            if (totalQuantity < (group.minimum ?? 0)) {
              errors[groupKey] = t('MenuManagementPreviewItemModal.validationSelectAtLeast', {
                minimum: group.minimum,
                groupName: group.name,
              });
            }
          }

          // Validation for checkbox group
          if (Array.isArray(groupModifiers)) {
            if (groupModifiers.length < (group.minimum ?? 0)) {
              errors[groupKey] = t('MenuManagementPreviewItemModal.validationSelectAtLeast', {
                minimum: group.minimum,
                groupName: group.name,
              });
            } else if ((group.maximum ?? 0) > 0 && groupModifiers.length > (group.maximum ?? 0)) {
              errors[groupKey] = t('MenuManagementPreviewItemModal.validationSelectNoMoreThan', {
                maximum: group.maximum,
                groupName: group.name,
              });
            }
          }
        });
        return errors;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- see comment above
      }) as any,
    },
  });

  // The full flat list (top-level + any nested groups the user has actually
  // selected into), independent of which screen is currently showing — used
  // for price and for the overall required-field validation. Must stay the
  // full list regardless of screen, or price would drop a nested selection's
  // contribution the moment the user backs out to the top-level screen.
  const fullModifierGroups = buildVisibleModifierGroups({
    itemKey: itemDetails?.key,
    itemType: 'items',
    menu: menu as PreviewMenu,
    getSortedChildren,
    formModifiers: form.values.modifiers,
  });

  const topLevelGroups = buildVisibleModifierGroups({
    itemKey: itemDetails?.key,
    itemType: 'items',
    menu: menu as PreviewMenu,
    getSortedChildren,
    formModifiers: {},
  });

  const getSelectedModifierNames = (nestedGroup: PreviewModifierGroup): string[] => {
    const groupValue = form.values.modifiers[nestedGroup.groupKey];
    if (!groupValue) return [];
    if (isCheckboxValue(groupValue)) {
      return nestedGroup.modifiers
        .filter(modifier => groupValue.some(selected => selected.key === modifier.key))
        .map(modifier => modifier.name ?? '');
    }
    if (isRadioGroupValue(groupValue)) {
      return nestedGroup.modifiers
        .filter(modifier => modifier.key === groupValue.key)
        .map(modifier => modifier.name ?? '');
    }
    if (isInputGroupValue(groupValue)) {
      return nestedGroup.modifiers
        .filter(modifier => (groupValue[modifier.key]?.quantity ?? 0) > 0)
        .map(modifier => modifier.name ?? '');
    }
    return [];
  };

  // Direct children - what a single drill-down screen should show.
  const getNestedGroupsFor = (modifierKey: string) =>
    buildVisibleModifierGroups({
      itemKey: modifierKey,
      itemType: 'modifiers',
      menu: menu as PreviewMenu,
      getSortedChildren,
      formModifiers: {},
    });

  // Every descendant level's selection combined
  const getSelectedNestedGroupsFor = (modifierKey: string) =>
    buildVisibleModifierGroups({
      itemKey: modifierKey,
      itemType: 'modifiers',
      menu: menu as PreviewMenu,
      getSortedChildren,
      formModifiers: form.values.modifiers,
    });

  const getGroupsAtPath = (path: Array<{ modifierKey: string }>): PreviewModifierGroup[] => {
    if (path.length === 0) return topLevelGroups;
    return getNestedGroupsFor(path[path.length - 1].modifierKey);
  };

  const decorateGroups = (groups: PreviewModifierGroup[]): PreviewModifierGroup[] =>
    groups.map(group => ({
      ...group,
      modifiers: group.modifiers.map((modifier: PreviewModifier) => {
        const hasNestedGroups = getNestedGroupsFor(modifier.key).length > 0;
        const nestedNames = hasNestedGroups
          ? getSelectedNestedGroupsFor(modifier.key).flatMap(getSelectedModifierNames)
          : [];
        return {
          ...modifier,
          hasNestedGroups,
          nestedSummary: nestedNames.length ? nestedNames.join(', ') : undefined,
        };
      }),
    }));

  const decoratedTopLevelGroups = decorateGroups(topLevelGroups);

  const nestedGroups = activeNestedModifier
    ? decorateGroups(getGroupsAtPath(activeNestedPath))
    : [];

  const visibleModifierGroups = activeNestedModifier ? nestedGroups : decoratedTopLevelGroups;

  const onModifierChange = (
    modifierGroupName: string,
    modifiedValues: ModifierGroupFormValue | undefined,
  ) => {
    form.setValues(currentValues => {
      const existingModifiers = currentValues.modifiers || {};

      return {
        ...currentValues,
        modifiers: {
          ...existingModifiers,
          [modifierGroupName]: modifiedValues,
        },
      };
    });

    form.clearFieldError(`modifiers.${modifierGroupName}`);
  };

  const handleDrillIn = (modifierKey: string, modifierName: string) => {
    setActiveNestedPath(prev => [...prev, { modifierKey, modifierName }]);
  };

  const handleBack = () => {
    if (activeNestedModifier) {
      const hasUnmetNestedGroup = nestedGroups.some(
        group =>
          !isModifierGroupRequirementMet({
            group,
            groupValue: form.values.modifiers[group.groupKey],
          }),
      );

      if (hasUnmetNestedGroup) {
        const parentGroups = getGroupsAtPath(activeNestedPath.slice(0, -1));
        const parentGroup = parentGroups.find(group =>
          group.modifiers.some(modifier => modifier.key === activeNestedModifier.modifierKey),
        );

        if (parentGroup) {
          onModifierChange(
            parentGroup.groupKey,
            deselectModifier({
              groupValue: form.values.modifiers[parentGroup.groupKey],
              modifierKey: activeNestedModifier.modifierKey,
            }),
          );
        }
      }
    }

    setActiveNestedPath(prev => prev.slice(0, -1));
  };

  const handleContinue = () => {
    if (!activeNestedModifier) return;

    const nestedGroupKeys = new Set(nestedGroups.map(group => group.groupKey));
    const previousModifierErrors = {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      ...((form.errors.modifiers as any) ?? {}),
    };
    nestedGroupKeys.forEach(groupKey => delete previousModifierErrors[groupKey]);

    const validationErrors = form.validate();
    const nestedFieldErrors = validationErrors.errors?.modifiers ?? {};
    const scopedNestedErrors = Object.fromEntries(
      Object.entries(nestedFieldErrors).filter(([groupKey]) => nestedGroupKeys.has(groupKey)),
    );

    form.setErrors({ modifiers: { ...previousModifierErrors, ...scopedNestedErrors } });

    if (Object.keys(scopedNestedErrors).length > 0) {
      setShowErrors(true);
      return;
    }

    setActiveNestedPath(prev => prev.slice(0, -1));
  };

  const handleFormSubmit = () => {
    setShowErrors(true);

    const validationErrors = form.validate();

    if (Object.keys(validationErrors.errors).length === 0) {
      form.clearErrors();
      setShowErrors(false);
    } else {
      form.setErrors(validationErrors.errors);
    }
  };

  const handleCloseModal = () => {
    onClose();
    onActionFinish();
  };

  const itemPrice = Number(itemDetails?.price?.price) || 0;
  const formModifiers = form.values.modifiers || {};
  const modifierSum = fullModifierGroups.reduce((acc, group) => {
    const groupModifiers = formModifiers[group.groupKey];
    if (!groupModifiers) return acc;

    if (isCheckboxValue(groupModifiers)) {
      return acc + groupModifiers.reduce((prev, mod) => prev + (mod.price || 0), 0);
    }
    if (isRadioGroupValue(groupModifiers)) {
      return acc + (groupModifiers?.price || 0);
    }
    if (isInputGroupValue(groupModifiers)) {
      return (
        acc +
        Object.values(groupModifiers).reduce(
          (prev, { price = 0, quantity: modifierQuantity = 0 }) => prev + price * modifierQuantity,
          0,
        )
      );
    }
    return acc;
  }, 0);

  const totalPrice = itemPrice + modifierSum;

  const price = formatCurrency((quantity * totalPrice) / 100);
  const buttonText = itemDetails?.paused
    ? t('MenuManagementPreviewItemModal.itemUnavailableButton')
    : t('MenuManagementPreviewItemModal.addButton', { price });

  return (
    <Modal
      opened={opened}
      onClose={handleCloseModal}
      title={itemDetails?.name}
      styles={{ body: { padding: 0 } }}
    >
      <Stack gap="md" px="xl">
        {!activeNestedModifier && (
          <>
            {titleImage}
            {(servingsLabel || classificationBadges.length > 0) && (
              <Group gap="xs3">
                {servingsLabel && (
                  <Badge variant="outline" size="xs">
                    {servingsLabel}
                  </Badge>
                )}
                {classificationBadges.map(label => (
                  <Badge key={label as string} variant="outline" size="xs">
                    {label}
                  </Badge>
                ))}
              </Group>
            )}
            {itemDetails?.description && (
              <Text
                style={{
                  wordBreak: 'break-word',
                }}
              >
                {itemDetails.description}
              </Text>
            )}
            <Divider />
          </>
        )}
        <form onSubmit={form.onSubmit(() => handleFormSubmit())}>
          {activeNestedModifier && (
            <>
              <Text mb="md" variant="title-sm">
                {activeNestedModifier.modifierName}
              </Text>
              <Divider mb="calc(var(--mantine-spacing-md) * 2)" />
            </>
          )}
          <ModifierGroup
            modifierGroups={visibleModifierGroups}
            onModifierChange={onModifierChange}
            onDrillIn={handleDrillIn}
            formModifiers={form.values.modifiers}
            formErrors={form.errors}
            showErrors={showErrors}
            hideLastDivider={!!activeNestedModifier}
          />
          {!activeNestedModifier && (
            <Textarea
              autosize
              label={t('MenuManagementPreviewItemModal.specialInstructionsLabel')}
              maxRows={3}
              minRows={3}
              mb="lg"
              placeholder={t('MenuManagementPreviewItemModal.specialInstructionsPlaceholder')}
              radius="xs"
            />
          )}
        </form>
      </Stack>
      <Dock stacked={false}>
        {activeNestedModifier ? (
          <Group justify="space-between" wrap="nowrap">
            <Button onClick={handleBack} variant="outline" size="md" style={{ flex: 1 }}>
              Back
            </Button>
            <Button onClick={handleContinue} size="md" style={{ flex: 1 }}>
              Continue
            </Button>
          </Group>
        ) : (
          <Group justify="space-between" wrap="nowrap">
            <Counter
              quantity={quantity}
              increment={increment}
              decrement={decrement}
              set={set}
              size="md"
              variant="outlined"
            />
            <Button
              onClick={handleFormSubmit}
              disabled={itemDetails?.paused}
              size="md"
              style={{ flex: 1 }}
            >
              {buttonText}
            </Button>
          </Group>
        )}
      </Dock>
    </Modal>
  );
};
